import * as React from 'react'

import { Box, Button, Table, Text, Tooltip, useToggle } from '@factorialco/deprecated-design-system'
import { TeamsListItemFragment } from 'generated/resources/graphql'

import { observer } from 'mobx-react'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import hasFeature from 'modules/core/lib/hasFeature'
import i18n from 'modules/core/lib/i18n'
import companies from 'modules/core/store/collections/companies'

import GenderStackedBar from 'modules/org_chart/components/GenderStackedBar'
import TeamAvatar from 'modules/teams/components/TeamAvatar'
import { useTeamsShiftLocations } from 'modules/teams/components/teamsShiftLocationContext'
import { getTimeLapseInYears } from 'modules/teams/lib/helpers'
import teamsAnalytics from 'modules/teams/store/collections/teamsAnalytics'

import WorkLocationStackedBar from '../../WorkLocationStackedBar'

interface Props {
  team: TeamsListItemFragment
  loadingAnalytics: boolean
  canEmployeeSeeAggregatedPersonalInfo: boolean
}

const TeamInsights: React.FC<Props> = ({
  team,
  loadingAnalytics,
  canEmployeeSeeAggregatedPersonalInfo,
}) => {
  const canSeeAggregatedInfo = canEmployeeSeeAggregatedPersonalInfo
  const hasWorkLocation = companies.current.attributes.has_clock_in_location_enabled
  const shiftLocations = useTeamsShiftLocations()

  const columns = hasWorkLocation ? 6 : 4

  if (!hasFeature('teams_insights')) {
    return null
  }

  if (loadingAnalytics) {
    return <Table.Cell colSpan={columns} />
  }

  const teamAnalytics = teamsAnalytics.find({ id: team.id })
  const average_age = teamAnalytics?.attributes.average_age
  const ageInYears = average_age?.error
    ? average_age?.error?.value
    : `${i18n.t('employees.common.age')}: ${average_age?.results?.[0]?.value} ${i18n.t(
        'common.years.other'
      )}`
  const tenure = teamAnalytics?.attributes.tenure
  const tenureDate = tenure?.results?.[0]?.value
  const tenureInYears = tenure?.error
    ? tenure?.error?.value
    : `${i18n.t('employees.common.tenure')}: ${tenureDate && getTimeLapseInYears(tenureDate)}`

  return (
    <>
      <Table.Cell colSpan={2}>
        <Box flexDirection="column" justifyContent="spaceBetween">
          <Text.Normal
            weight="normal"
            size="200"
            color={average_age?.error ? 'grey600' : 'grey1000'}
          >
            {ageInYears}
          </Text.Normal>
          <Text.Normal
            weight="normal"
            size="100"
            color={average_age?.error ? 'grey600' : 'grey1000'}
          >
            {tenureInYears}
          </Text.Normal>
        </Box>
      </Table.Cell>
      {hasWorkLocation && (
        <Table.Cell colSpan={2}>
          <Box flexDirection="row" alignItems="center" justifyContent="spaceBetween">
            {teamAnalytics && (
              <WorkLocationStackedBar id={team.id} shiftLocations={shiftLocations} />
            )}
          </Box>
        </Table.Cell>
      )}
      {canSeeAggregatedInfo && (
        <Table.Cell colSpan={2}>
          <Box flexDirection="row" alignItems="center" justifyContent="spaceBetween">
            {teamAnalytics && (
              <GenderStackedBar
                results={teamAnalytics?.attributes.diversity.results}
                error={teamAnalytics?.attributes.diversity.error?.message}
              />
            )}
          </Box>
        </Table.Cell>
      )}
    </>
  )
}

const Item: React.FC<Props> = ({
  team,
  loadingAnalytics,
  canEmployeeSeeAggregatedPersonalInfo,
}) => {
  const today = new Date()
  const activeMemberships = team.memberships.filter((member) => {
    const terminatedOn =
      member?.employee?.terminatedOn && new Date(member?.employee?.terminatedOn).getTime()
    return !member?.employee?.terminatedOn || (terminatedOn && terminatedOn >= today.getTime())
  })
  const { navigation } = useNavigation()
  const [open, toggle] = useToggle(false)

  return (
    <Table.Row.Router to={navigation.employees.teams.id.fullPath({ id: team.id })}>
      <Table.Cell colSpan={3}>
        <Box
          overflowX="hidden"
          flexDirection="row"
          gap="s24"
          alignItems="center"
          paddingRight="s24"
        >
          <TeamAvatar team={team} />
          <Box overflowX="hidden">
            <Tooltip.Alt
              attachment="bottom center"
              toggle={toggle}
              open={open}
              offset="0 -10px"
              label={team.name}
              textAlign="left"
            >
              <Text.Normal weight="normal" whiteSpace="breakSpaces" color="grey1000" ellipsis>
                {team.name}
              </Text.Normal>
            </Tooltip.Alt>
          </Box>
        </Box>
      </Table.Cell>
      <Table.Cell colSpan={1}>
        <Box flexDirection="row" alignItems="center" justifyContent="spaceBetween">
          <Text.Normal
            weight="normal"
            color={activeMemberships.length > 0 ? 'grey1000' : 'grey600'}
          >
            {activeMemberships.length}
          </Text.Normal>
        </Box>
      </Table.Cell>
      <TeamInsights
        team={team}
        loadingAnalytics={loadingAnalytics}
        canEmployeeSeeAggregatedPersonalInfo={canEmployeeSeeAggregatedPersonalInfo}
      />
      <Table.Cell colSpan={1}>
        <Box alignItems="flexEnd">
          <Button
            onClick={() => track(trackingEvents.teams.goToTeamsDetails)}
            type="secondary"
            iconName="arrow-right"
          />
        </Box>
      </Table.Cell>
    </Table.Row.Router>
  )
}

export default observer(Item)
